import { html, nothing, TemplateResult } from 'lit';

import '../ns-icon/ns-icon.js';

/** The mark drawn inside a provider button's green circle. */
export type ProviderIcon = 'google' | 'microsoft' | 'email' | 'invitation';

function renderProviderIcon(icon?: ProviderIcon) {
  switch (icon) {
    case 'google':
      return html`
        <img alt="" src="/images/google.svg" />
      `;
    case 'microsoft':
      return html`
        <img alt="" src="/images/microsoft.svg" />
      `;
    case 'email':
      return html`
        <ns-icon icon="email"></ns-icon>
      `;
    case 'invitation':
      return html`
        <ns-icon icon="person-add"></ns-icon>
      `;
    default:
      return nothing;
  }
}

export function renderProviderButton(
  label: string,
  onClick: () => unknown,
  className = 'white mb-3',
  icon?: ProviderIcon,
): TemplateResult {
  return html`
    <wa-button class=${className} @click=${onClick}>
      <div class="layout horizontal center w-100">
        <div class="green-dot mr-2" aria-hidden="true">${renderProviderIcon(icon)}</div>
        <span class="mr-1 flex">${label}</span>
        <img alt="" aria-hidden="true" src="/images/onboarding/arrow-right.svg" />
      </div>
    </wa-button>
  `;
}

export interface EmailCodeCardOptions {
  inputId: string;
  subtitle: TemplateResult | string;
  onVerify: () => unknown;
}

export function renderEmailCodeCard({ inputId, subtitle, onVerify }: EmailCodeCardOptions): TemplateResult {
  const formId = `${inputId}-form`;
  return html`
    <div class="stage-card">
      <div class="layout vertical">
        <div class="card-title flex">Email Verification</div>
        <div class="card-subtitle">${subtitle}</div>
      </div>
      <form
        id=${formId}
        novalidate
        @submit=${(e: Event) => {
          e.preventDefault();
          onVerify();
        }}
      >
        <wa-otp-input id=${inputId} label="Enter the 6-digit code:" length="6" type="numeric"></wa-otp-input>
      </form>
      <div class="layout horizontal mt-3">
        <wa-button variant="success" name="stage-register-email-code-proceed" type="submit" form=${formId}>
          Verify Code
        </wa-button>
      </div>
    </div>
  `;
}

export function isValidEmailCode(code: string): boolean {
  return /^\d{6}$/.test(code.trim());
}

export const EMAIL_LINK_PATH = '/login/email-link';

export function emailLinkToken(url: URL): string | null {
  if (url.pathname.replace(/\/+$/, '') !== EMAIL_LINK_PATH) return null;
  return url.searchParams.get('token') || null;
}

export interface EmailLinkCardOptions {
  failed: boolean;
  onLogIn: () => unknown;
  onRequestNew: () => unknown;
}

// Logging in takes a click, so a mail scanner opening the link can't use it up
export function renderEmailLinkCard({ failed, onLogIn, onRequestNew }: EmailLinkCardOptions): TemplateResult {
  return html`
    <div class="stage-card">
      <div class="layout vertical">
        <div class="card-title flex">Log in to nodeStream</div>
        <div class="card-subtitle">
          ${failed ? 'This link has expired or has already been used.' : 'Use the button below to finish logging in.'}
        </div>
      </div>
      <div class="layout horizontal mt-3">
        ${failed
          ? html`
              <wa-button variant="success" name="email-link-request-new" @click=${onRequestNew}>
                Request a new code
              </wa-button>
            `
          : html`
              <wa-button variant="success" name="email-link-log-in" @click=${onLogIn}>Log in</wa-button>
            `}
      </div>
    </div>
  `;
}
